export type AttendanceStatus = 'PRESENT' | 'ABSENT' | 'EXCUSED' | 'PENDING';
interface GP {
  studentId: string;
  studentName: string;
  studentNickname: string | null;
  attendance: string;
  enrollmentId?: string;
  fromSlot?: boolean;
}
interface GS { id?: string; startAt: string; status?: string; participants: GP[] }
export interface AttendanceGrid {
  students: { studentId: string; studentName: string; studentNickname: string | null }[];
  dates: string[];
  cell: (studentId: string, date: string) => AttendanceStatus | null;
  /** Session id backing a given (studentId, date) cell, if known — used to link unmarked cells to `/sessions/:id`. */
  sessionIdAt: (studentId: string, date: string) => string | null;
  pendingByStudent: Record<string, number>;
  /** Sessions marked Có phép, whatever the session: the P cells on the row. */
  excusedByStudent: Record<string, number>;
  /** Sessions marked Vắng: the ✗ cells on the row. */
  absentByStudent: Record<string, number>;
  /**
   * Excused absences made up, the "đã bù n" on the attendance chip: for each
   * enrollment `min(attended ad-hoc sessions, excused official sessions)`, the
   * clamp `excusedQuotaDisplay` applies, summed over the student's enrollments.
   * Computed from the marks in these sessions, so a subject filter narrows it.
   * `excusedByStudent − madeUpByStudent` is the chip's net whenever every P is
   * on an official session (a P on an ad-hoc session burns no phép).
   */
  madeUpByStudent: Record<string, number>;
}
const dateKey = (iso: string) => iso.slice(0, 10);

export function pivotAttendanceGrid(sessions: GS[]): AttendanceGrid {
  const live = sessions.filter((s) => s.status !== 'CANCELLED');
  const studentMap = new Map<string, { studentName: string; studentNickname: string | null }>();
  const dateSet = new Set<string>();
  const byCell = new Map<string, AttendanceStatus>(); // key `${studentId}|${date}`
  const sessionByCell = new Map<string, string>(); // key `${studentId}|${date}`
  const pendingByStudent: Record<string, number> = {};
  const excusedByStudent: Record<string, number> = {};
  const absentByStudent: Record<string, number> = {};
  // Keyed by enrollment: the clamp is per enrollment, a student in two
  // subjects cannot make up one subject's absence with the other's session.
  const quota = new Map<string, { studentId: string; excused: number; attendedAdHoc: number }>();
  const bump = (m: Record<string, number>, k: string) => { m[k] = (m[k] ?? 0) + 1; };
  for (const s of live) {
    const d = dateKey(s.startAt);
    dateSet.add(d);
    for (const p of s.participants) {
      studentMap.set(p.studentId, { studentName: p.studentName, studentNickname: p.studentNickname });
      byCell.set(`${p.studentId}|${d}`, p.attendance as AttendanceStatus);
      if (s.id) sessionByCell.set(`${p.studentId}|${d}`, s.id);
      if (p.attendance === 'PENDING') bump(pendingByStudent, p.studentId);
      if (p.attendance === 'EXCUSED') bump(excusedByStudent, p.studentId);
      if (p.attendance === 'ABSENT') bump(absentByStudent, p.studentId);
      const key = p.enrollmentId ?? p.studentId;
      const q = quota.get(key) ?? { studentId: p.studentId, excused: 0, attendedAdHoc: 0 };
      if (p.attendance === 'EXCUSED' && p.fromSlot !== false) q.excused += 1;
      if (p.attendance === 'PRESENT' && p.fromSlot === false) q.attendedAdHoc += 1;
      quota.set(key, q);
    }
  }
  const madeUpByStudent: Record<string, number> = {};
  for (const q of quota.values()) {
    const madeUp = Math.min(q.excused, q.attendedAdHoc);
    if (madeUp > 0) madeUpByStudent[q.studentId] = (madeUpByStudent[q.studentId] ?? 0) + madeUp;
  }
  return {
    students: [...studentMap.entries()].map(([studentId, v]) => ({ studentId, ...v })),
    dates: [...dateSet].sort(),
    cell: (studentId, date) => byCell.get(`${studentId}|${date}`) ?? null,
    sessionIdAt: (studentId, date) => sessionByCell.get(`${studentId}|${date}`) ?? null,
    pendingByStudent,
    excusedByStudent,
    absentByStudent,
    madeUpByStudent,
  };
}
